import React, { useState } from 'react';
import { IoChevronUp, IoChevronDown } from 'react-icons/io5';
import { FaHeart, FaObjectUngroup } from 'react-icons/fa6';
import { usePromptInteractions } from '../hooks/usePromptInteractions';
import './PromptCard.css';

/**
 * Shared component for prompt action buttons (upvote, downvote, favorite, duplicate)
 * @param {Object} props
 * @param {Object} props.prompt - The prompt object
 * @param {Function} props.onUpdate - Optional callback when prompt is updated (for voting)
 * @param {Function} props.onFavoriteToggle - Optional callback when favorite is toggled (prompt, isFavorited)
 * @param {Function} props.onDuplicate - Optional callback when duplicate is clicked (prompt)
 * @param {boolean} props.stopPropagation - Whether to stop event propagation (default: false)
 * @param {string} props.className - Optional additional CSS class name
 * @param {string} props.buttonClassName - Optional CSS class for buttons (default: 'action-button')
 * @param {boolean} props.hideFavorite - Whether to hide the favorite button (default: false)
 * @param {boolean} props.hideDuplicate - Whether to hide the duplicate button (default: false)
 * @param {boolean} props.isDuplicate - Whether this prompt is already a duplicate (default: false)
 */
const PromptActionButtons = ({ prompt, onUpdate, onFavoriteToggle, onDuplicate, stopPropagation = false, className = '', buttonClassName = 'action-button', hideFavorite = false, hideDuplicate = false, isDuplicate = false }) => {
  const [showDuplicateTooltip, setShowDuplicateTooltip] = useState(false);
  const {
    isUpvoted,
    isDownvoted,
    isFavorited,
    upvotes,
    downvotes,
    handleUpvote,
    handleDownvote,
    handleFavorite,
  } = usePromptInteractions(prompt, onUpdate, onFavoriteToggle);

  const handleClick = (handler) => (e) => {
    if (stopPropagation && e?.stopPropagation) {
      e.stopPropagation();
    }
    return handler(e);
  };

  return (
    <>
      <button
        className={`${buttonClassName} upvote-button ${isUpvoted ? 'active' : ''}`}
        onClick={handleClick(handleUpvote)}
        aria-label="Upvote"
      >
        <IoChevronUp className="action-icon" />
        <span className="action-text">{upvotes}</span>
      </button>
      
      <button
        className={`${buttonClassName} downvote-button ${isDownvoted ? 'active' : ''}`}
        onClick={handleClick(handleDownvote)}
        aria-label="Downvote"
      >
        <IoChevronDown className="action-icon" />
        <span className="action-text">{downvotes}</span>
      </button>
      
      {!hideFavorite && (
        <button
          className={`${buttonClassName} favorite-button ${isFavorited ? 'active' : ''}`}
          onClick={handleClick(handleFavorite)}
          aria-label="Favorite"
        >
          <FaHeart className="action-icon" />
        </button>
      )}
      
      {!hideDuplicate && !isDuplicate && onDuplicate && (
        <div className="duplicate-button-wrapper" style={{ position: 'relative' }}>
          <button
            className={`${buttonClassName} duplicate-button`}
            onClick={handleClick(() => onDuplicate(prompt))}
            onMouseEnter={() => setShowDuplicateTooltip(true)}
            onMouseLeave={() => setShowDuplicateTooltip(false)}
            aria-label="Duplicate prompt"
          >
            <FaObjectUngroup className="action-icon" />
          </button>
          {showDuplicateTooltip && (
            <div className="duplicate-tooltip">Duplicate prompt</div>
          )}
        </div>
      )}
    </>
  );
};

export default PromptActionButtons;

